import React, { useState } from 'react';
import './IceaLionAcademyForm.css';
import { Liferay } from '../common/services/liferay/liferay.js';
import api from '../common/services/liferay/api.js';
import DebugInfo from './DebugInfo.jsx';
import SuccessModal from './SuccessModal.jsx';

const IceaLionAcademyForm = () => {
  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    middleName: '',
    lastName: '',
    emailAddress: '',
    primaryPhoneNumber: '',
    location: '',
    numberOfPoliciesSold: '',
    cvFile: null
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const titleOptions = [
    'Mr',
    'Mrs',
    'Miss',
    'Ms',
    'Dr',
    'Prof'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing - only if error exists
    setErrors(prev => {
      if (prev[name]) {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
      return prev;
    });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    
    // Clear previous errors for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // Validate file type and size
    if (file) {
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          [name]: 'Please upload a PDF or Word document'
        }));
        // Clear the file input
        e.target.value = '';
        return;
      }
      
      if (file.size > maxSize) {
        setErrors(prev => ({
          ...prev,
          [name]: 'File size must be less than 5MB'
        }));
        // Clear the file input
        e.target.value = '';
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: file
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Title validation - Mandatory; options like Mr., Ms., Mrs., Dr.
    if (!formData.title) {
      newErrors.title = 'Title is required';
    }

    // First Name validation - Mandatory; alphabetic only; max 50 chars
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First Name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName)) {
      newErrors.firstName = 'First Name must contain only alphabetic characters';
    } else if (formData.firstName.length > 50) {
      newErrors.firstName = 'First Name must not exceed 50 characters';
    }

    // Middle Name validation - Optional; alphabetic only; max 50 chars
    if (formData.middleName.trim() && !/^[a-zA-Z\s]+$/.test(formData.middleName)) {
      newErrors.middleName = 'Middle Name must contain only alphabetic characters';
    } else if (formData.middleName.length > 50) {
      newErrors.middleName = 'Middle Name must not exceed 50 characters';
    }

    // Last Name validation - Mandatory; alphabetic only; max 50 chars
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last Name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName)) {
      newErrors.lastName = 'Last Name must contain only alphabetic characters';
    } else if (formData.lastName.length > 50) {
      newErrors.lastName = 'Last Name must not exceed 50 characters';
    }

    // Email validation - Mandatory; email format validation
    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = 'Email Address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.emailAddress)) {
      newErrors.emailAddress = 'Email Address is invalid';
    }

    // Phone validation - Mandatory; 8-15 digits; allow country code
    if (!formData.primaryPhoneNumber.trim()) {
     newErrors.primaryPhoneNumber = 'Primary Phone Number is required';
    } else {
      const cleanPhone = String(formData.primaryPhoneNumber).replace(/[^\d+]/g, '').trim();
      const phoneRegex = /^\+?\d{8,15}$/;
    if (!phoneRegex.test(cleanPhone)) {
      newErrors.primaryPhoneNumber = 'Primary Phone number must be 8-15 digits (country code optional)';
    }
  }

    // Location validation - Mandatory; free text or pre-defined list
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    // Number of Policies Sold validation - Mandatory; numeric validation; max 6 digits
    if (!formData.numberOfPoliciesSold.trim()) {
      newErrors.numberOfPoliciesSold = 'Number of Policies Sold is required';
    } else if (parseInt(formData.numberOfPoliciesSold) < 0) {
      newErrors.numberOfPoliciesSold = 'Number cannot be negative';
    } else if (!/^\d{1,6}$/.test(formData.numberOfPoliciesSold)) {
      newErrors.numberOfPoliciesSold = 'Must be a number with maximum 6 digits';
    }

    // CV file validation - Mandatory; PDF/DOC/DOCX; max 5MB
    if (!formData.cvFile) {
      newErrors.cvFile = 'CV is required';
    }

    // Use functional update to ensure we're working with latest state
    setErrors(() => newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isValid = validateForm();
    if (!isValid) {
      // Errors are set by validateForm, just return
      return;
    }

    setIsSubmitting(true);
    setErrors({}); // Clear any previous errors
    
    try {
      // Submit to Liferay API (you'll need to create an academy application API endpoint)
      const result = await api.createAcademyApplication(formData);
      
      
      // Reset form after successful submission
      setFormData({
        title: '',
        firstName: '',
        middleName: '',
        lastName: '',
        emailAddress: '',
        primaryPhoneNumber: '',
        location: '',
        numberOfPoliciesSold: '',
        cvFile: null
      });
      
      // Reset file inputs
      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach(input => {
        input.value = '';
      });
      
      // Show success modal
      setShowSuccessModal(true);
      
    } catch (error) {
      if (typeof process === 'undefined' || process.env?.NODE_ENV !== 'test') {
        console.error('Error submitting academy application:', error);
      }
      
      let errorMessage = 'Failed to submit academy application. Please try again.';
      
      // Handle specific error cases
      if (error.message) {
        // Prioritize auth errors even if wrapped as "API Error (Status: 401)"
        if (error.message.includes('401') || error.message.includes('403')) {
          errorMessage = 'Authentication failed. Please refresh the page and try again.';
        } else if (error.message.includes('API Error')) {
          // Keep default message for API errors
          errorMessage = 'Failed to submit academy application. Please try again.';
        } else if (error.message.includes('400')) {
          errorMessage = 'Invalid data provided. Please check your input and try again.';
        } else if (error.message.includes('500')) {
          errorMessage = 'Server error occurred. Please try again later.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  const getFileName = (file) => {
    return file ? file.name : '';
  };

  return (
    <div className="academy-form-container">
      <DebugInfo />
      <div className="academy-form-card">
        
       
        <form onSubmit={handleSubmit} className="academy-form">
          <div className="form-row">
            <div className="form-group half-width">
              <label htmlFor="academyTitle" className="form-label">Title *</label>
              <select
                id="academyTitle"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`form-select ${errors.title ? 'error' : ''}`}
                disabled={isSubmitting}
              >
                <option value="">Select Title</option>
                {titleOptions.map(title => (
                  <option key={title} value={title}>{title}</option>
                ))}
              </select>
              {errors.title && <span className="field-error">{errors.title}</span>}
            </div>

            <div className="form-group half-width">
              <label className="form-label">First Name *</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Jane"
                className={`form-input ${errors.firstName ? 'error' : ''}`}
                disabled={isSubmitting}
                maxLength={50}
                pattern="[a-zA-Z\s]+"
                title="First Name must contain only alphabetic characters, max 50 characters"
              />
              {errors.firstName && <span className="field-error">{errors.firstName}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group half-width">
              <label className="form-label">Middle Name</label>
              <input
                type="text"
                name="middleName"
                value={formData.middleName}
                onChange={handleChange}
                placeholder="Doe"
                className={`form-input ${errors.middleName ? 'error' : ''}`}
                disabled={isSubmitting}
                maxLength={50}
                pattern="[a-zA-Z\s]*"
                title="Middle Name must contain only alphabetic characters, max 50 characters"
              />
              {errors.middleName && <span className="field-error">{errors.middleName}</span>}
            </div>

            <div className="form-group half-width">
              <label className="form-label">Last Name *</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Smith"
                className={`form-input ${errors.lastName ? 'error' : ''}`}
                disabled={isSubmitting}
                maxLength={50}
                pattern="[a-zA-Z\s]+"
                title="Last Name must contain only alphabetic characters, max 50 characters"
              />
              {errors.lastName && <span className="field-error">{errors.lastName}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group half-width">
              <label className="form-label">Email Address *</label>
              <input
                type="email"
                name="emailAddress"
                value={formData.emailAddress}
                onChange={handleChange}
                placeholder="Jane@doe.com"
                className={`form-input ${errors.emailAddress ? 'error' : ''}`}
                disabled={isSubmitting}
              />
              {errors.emailAddress && <span className="field-error">{errors.emailAddress}</span>}
            </div>

            <div className="form-group half-width">
              <label className="form-label">Primary Phone Number *</label>
              <input
                type="tel"
                name="primaryPhoneNumber"
                value={formData.primaryPhoneNumber}
                onChange={handleChange}
                placeholder="0712 345 678"
                className={`form-input ${errors.primaryPhoneNumber ? 'error' : ''}`}
                disabled={isSubmitting}
                pattern="^\+?[0-9]{8,15}$"
                title="Phone number must be 8-15 digits (country code optional)"
              />
              {errors.primaryPhoneNumber && <span className="field-error">{errors.primaryPhoneNumber}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group half-width">
              <label className="form-label">Location *</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Current Location"
                className={`form-input ${errors.location ? 'error' : ''}`}
                disabled={isSubmitting}
              />
              {errors.location && <span className="field-error">{errors.location}</span>}
            </div>

            <div className="form-group half-width">
              <label className="form-label">Number of Policies Sold *</label>
              <input
                type="number"
                name="numberOfPoliciesSold"
                value={formData.numberOfPoliciesSold}
                onChange={handleChange}
                placeholder="0"
                min="0"
                max="999999"
                className={`form-input ${errors.numberOfPoliciesSold ? 'error' : ''}`}
                disabled={isSubmitting}
                pattern="\d{1,6}"
                title="Must be a number with maximum 6 digits"
              />
              {errors.numberOfPoliciesSold && <span className="field-error">{errors.numberOfPoliciesSold}</span>}
            </div>
          </div>
          <div className="form-group file-upload-group">
            <label className="form-label">Attach CV</label>
            <div className="file-upload-container">
              <input
                type="file"
                name="cvFile"
                id="academyCvFile"
                onChange={handleFileChange}
                className="file-input"
                disabled={isSubmitting}
              />
              <label htmlFor="academyCvFile" className="file-label">
                <span className="file-label-text">Attach CV</span>
                <span className="file-button">Choose file</span>
              </label>
              {formData.cvFile && (
                <span className="file-name">{getFileName(formData.cvFile)}</span>
              )}
            </div>
            {errors.cvFile && <span className="field-error">{errors.cvFile}</span>}
          </div>
           {errors.submit && (
            <div className="field-error">
              {errors.submit}
            </div>
          )}
          <button 
            type="button"
            onClick={handleSubmit}
            className="btn primary-button regular"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Apply Online'}
          </button>
        </form>
      </div>
      
      <SuccessModal 
        isOpen={showSuccessModal} 
        onClose={handleCloseModal}
        title="Academy Application Submitted!"
        message="Thank you for your interest in our Academy. Your submission has been received and we will contact you with more details soon."
      />
    </div>
  );
};

export default IceaLionAcademyForm;
